import { ErrorBoundary } from "react-error-boundary";
import { Building2, Eye, Settings, Clock, MapPin, BuildingIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { ClientView } from "./ClientView";
import { ClientManage } from "./ClientManage";
import { ClientHistory } from "./ClientHistory";
import { AdimplenciaTag } from "@/components/shared/AdimplenciaTag";
import {
  Client as ApiClient,
} from "@/api/clientService";
import type { Products } from "@/lib/api";
import { useCallback, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCycleRequiredHandler } from "@/hooks/useCycleRequiredHandler";
import { CyclePickerDialog } from "@/components/shared/CyclePickerDialog";
import { commercialCyclesApi, CommercialCycle, profileClientApi } from "@/lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

type UIClient = {
  id: number;
  name: string;
  code?: string;
  cnpj?: string;
  city?: string;
  state?: string;
  products?: Products[];
  events?: unknown[];
  shipping_fee?: number;
  clientAdjustment?: number;
  paymentPlanId?: number;
  maxInstallments?: number;
  paymentConfigurationIds?: string[];
  createZendeskTicketOnOrder?: boolean;
};

type ActiveTab = "ver" | "gerenciar" | "historico";

interface ClientsAccordionProps {
  clients: ApiClient[];
  expandedClient: number | null;
  onExpand: (val: number | null) => void;
  activeTab: ActiveTab;
  onTabChange: (val: ActiveTab) => void;
}

// ---------------------------------------------------------------------------
// Constantes
// ---------------------------------------------------------------------------

/** Mapeamento dos status da API para labels em PT-BR. */
const CYCLE_STATUS_LABELS: Record<CommercialCycle["status"], string> = {
  Draft: "Rascunho",
  Published: "Publicado",
  Open: "Aberto",
  Closing: "Em fechamento",
  Closed: "Encerrado",
};

/** Fallback para o ErrorBoundary de cada item do accordion. */
const Fallback = ({ error }: { error: Error }) => (
  <div className="p-4 text-center rounded-md bg-red-50 text-red-700 border border-red-200">
    <p className="font-semibold">Erro ao carregar esta seção</p>
    <p className="text-sm mt-1">{error.message}</p>
  </div>
);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Normaliza um ApiClient para a forma interna UIClient, centralizando os casts. */
function toUIClient(client: ApiClient): UIClient {
  const raw = client as Record<string, unknown>;
  return {
    id: client.clientId ?? 0,
    name: client.clientFantasyName ?? "Cliente sem nome",
    code: client.clientCodeCnx ?? "-",
    cnpj: raw.clientCnpj as string | undefined,
    city: client.clientCity ?? "-",
    state: client.clientState ?? "-",
    products: (raw.products as Products[]) ?? [],
    events: (raw.history as unknown[]) ?? [],
    shipping_fee: (raw.shippingFee as number) || 0,
    clientAdjustment:
      (raw.clientAdjustment as number) ?? (raw.adjustment as number) ?? 0,
    paymentPlanId: raw.paymentPlanId as number | undefined,
    maxInstallments: raw.maxInstallments as number | undefined,
    paymentConfigurationIds: (raw.paymentConfigurationIds as string[]) ?? [],
    createZendeskTicketOnOrder: (raw.createZendeskTicketOnOrder as boolean | undefined) ?? false,
  };
}

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

export function ClientsAccordion({
  clients = [],
  expandedClient,
  onExpand,
  activeTab,
  onTabChange,
}: ClientsAccordionProps) {
  const queryClient = useQueryClient();

  const {
    selectedCycleYear,
    setSelectedCycleYear,
    availableCycles,
    dialogOpen: cycleDialogOpen,
    setDialogOpen: setCycleDialogOpen,
    handleCycleError,
    selectCycle,
  } = useCycleRequiredHandler();

  const { data: commercialCycles = [] } = useQuery<CommercialCycle[]>({
    queryKey: ["commercial-cycles"],
    queryFn: async () => {
      const response = await commercialCyclesApi.list();
      return response.data || [];
    },
  });

  /** Todos os ciclos (incluindo Rascunho), ordenados do mais recente ao mais antigo — memoizado para evitar sort a cada render. Filtro de status do backoffice traz todos, diferente do Portal do Cliente. */
  const sortedCycles = useMemo(
    () => [...commercialCycles].sort((a, b) => b.year - a.year),
    [commercialCycles],
  );

  /** Seleciona automaticamente o ciclo mais recente na primeira carga. */
  useEffect(() => {
    if (sortedCycles.length > 0 && selectedCycleYear === null) {
      setSelectedCycleYear(sortedCycles[0].year);
    }
  }, [sortedCycles, selectedCycleYear, setSelectedCycleYear]);

  /* ─── Query: Produtos do cliente expandido ─── */
  const {
    data: products = [],
    isFetching: loading,
  } = useQuery<Products[]>({
    queryKey: ["clientProducts", expandedClient, selectedCycleYear],
    queryFn: async () => {
      if (!expandedClient) return [];
      try {
        const { data } = await profileClientApi.getProfileDetails(
          expandedClient,
          selectedCycleYear ?? undefined,
        );
        if (!Array.isArray(data)) return [];
        // A API retorna agrupado por materialUseName; achata para uma lista plana
        return data.flatMap((group) =>
          group.products.map((p) => ({
            ...p,
            materialUseName: group.materialUseName,
          }))
        );
      } catch (error) {
        if (!handleCycleError(error)) {
          console.error("Erro ao carregar produtos do cliente:", error);
        }
        return [];
      }
    },
    enabled: !!expandedClient,
    staleTime: 0,
    gcTime: 0
  });

  /** Força refetch dos produtos após uma mutação feita pelo filho. */
  const handleProductUpdate = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ["clientProducts", expandedClient, selectedCycleYear],
    });
  }, [queryClient, expandedClient, selectedCycleYear]);

  if (!Array.isArray(clients) || clients.length === 0) {
    return (
      <p className="text-center text-gray-500 mt-4">
        Nenhum cliente encontrado.
      </p>
    );
  }

  return (
    <>
      <CyclePickerDialog
        open={cycleDialogOpen}
        availableCycles={availableCycles}
        onOpenChange={setCycleDialogOpen}
        onSelect={selectCycle}
      />

      <Accordion
        type="single"
        collapsible
        value={expandedClient?.toString() ?? ""}
        className="space-y-3"
        onValueChange={(val) => onExpand(val ? Number(val) : null)}
      >
        {clients.map((client, index) => {
          const uiClient = toUIClient(client);
          const { id, name, city, state } = uiClient;
          const raw = client as Record<string, unknown>;

          return (
            <AccordionItem
              key={`${index}-${id}`}
              value={id.toString()}
              className="border rounded-xl shadow-sm px-4 group hover:shadow-lg transition-all duration-300 border-l-4 border-l-primary/20 hover:border-l-primary"
            >
              <AccordionTrigger asChild className="hover:no-underline py-4 cursor-pointer">
                <div className="flex items-center w-full justify-between">
                  {/* Cabeçalho do cliente */}
                  <div className="flex items-center gap-4">
                    <div className="bg-blue-100 p-2 rounded-lg">
                      <Building2 className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <div className="flex gap-2">
                        <h3 className="font-semibold text-lg">{name}</h3>
                        <Badge variant="outline" className="text-xs">
                          {uiClient.code}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <BuildingIcon className="h-4 w-4" />
                        {uiClient.cnpj}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <MapPin className="h-4 w-4" />
                        {city}, {state}
                      </div>
                    </div>
                  </div>

                  <div className="w-auto flex items-center gap-4">
                    <AdimplenciaTag isInadimplente={client.isInadimplente} />

                    <div className="text-right mr-4">
                      <div className="text-sm text-muted-foreground mb-1">ACV</div>
                      <div className="text-2xl font-bold text-primary">
                        {new Intl.NumberFormat("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        }).format((raw.totalPurchaseOrderValue as number) || 0)}
                      </div>
                    </div>

                    {/* Seletor de ciclo comercial */}
                    {expandedClient === id && (
                      <div onClick={(e) => e.stopPropagation()}>
                        <Select
                          value={selectedCycleYear ? String(selectedCycleYear) : ""}
                          onValueChange={(v) => setSelectedCycleYear(Number(v))}
                        >
                          <SelectTrigger
                            className="w-[170px] h-9 text-sm"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <SelectValue placeholder="Selecione o ciclo" />
                          </SelectTrigger>
                          <SelectContent>
                            {sortedCycles.map((c) => (
                              <SelectItem key={c.year} value={String(c.year)}>
                                Ciclo {c.year}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {/* Tabs de ação */}
                    {expandedClient === id && (
                      <div className="flex gap-2 mr-2 transition-opacity duration-300 opacity-100">
                        <Button
                          size="sm"
                          variant={activeTab === "ver" ? "default" : "outline"}
                          onClick={(e) => {
                            e.stopPropagation();
                            onTabChange("ver");
                          }}
                        >
                          <Eye className="h-4 w-4 mr-1" /> Ver
                        </Button>

                        <Button
                          size="sm"
                          variant={activeTab === "gerenciar" ? "default" : "outline"}
                          onClick={(e) => {
                            e.stopPropagation();
                            onTabChange("gerenciar");
                          }}
                        >
                          <Settings className="h-4 w-4 mr-1" /> Gerenciar
                        </Button>

                        <Button
                          size="sm"
                          variant={activeTab === "historico" ? "default" : "outline"}
                          onClick={(e) => {
                            e.stopPropagation();
                            onTabChange("historico");
                          }}
                        >
                          <Clock className="h-4 w-4 mr-1" /> Histórico
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </AccordionTrigger>

              {/* Conteúdo interno */}
              <AccordionContent className="pb-4">
                <ErrorBoundary FallbackComponent={Fallback}>
                  {activeTab === "ver" && (
                    <ClientView
                      client={uiClient}
                      products={products}
                      loading={loading}
                      cycleYear={selectedCycleYear}
                    />
                  )}
                  {activeTab === "gerenciar" && (
                    <ClientManage
                      client={uiClient}
                      products={products}
                      loading={loading}
                      onProductUpdate={handleProductUpdate}
                    />
                  )}
                  {activeTab === "historico" && (
                    <ClientHistory clientCode={client.clientId} clientName={uiClient.name} />
                  )}
                </ErrorBoundary>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </>
  );
}

export default ClientsAccordion;
